import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, CircularProgress, Snackbar, Alert } from "@mui/material";
import { AiOutlineUser, AiOutlineShareAlt, AiOutlineArrowLeft } from "react-icons/ai";
import FallbackImage from "../../components/common/FallbackImage/FallbackImage";
import Tabs from "../../components/common/Tabs/Tabs";
import styles from "./MemoryView.module.css";
import { apiFetch, API_BASE } from "../../utils/api";
import * as Sentry from "@sentry/react";

const tabs = ["Details", "Activity"];

const MemoryView = () => {
  const { descriptor } = useParams();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState("Details");
  const [memory, setMemory] = useState(null);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [views, setViews] = useState(0);
  const [sales, setSales] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchMemoryDetails = async () => {
      try {
        const res = await apiFetch(`/api/memory/${descriptor}/details`,{}, false, true);
        setMemory(res.memory);
        setLikes(res.likes);
        setLiked(res.likedByUser);
        setViews(res.views);
        setSales(res.sales || []);
      } catch (err) {
        Sentry.captureException(err);
        console.error("Failed to load memory details", err);
      }
    };

    fetchMemoryDetails();
  }, [descriptor]);

  const handleTabChange = (tab) => setSelectedTab(tab);

  const handleGoBack = () => navigate(-1);

  const handleCheckPage = () => {
    navigate(`/nft-buy/${descriptor}`);
  };

  const handleSharePage = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/nft-buy/${descriptor}`);
      setSnackbarOpen(true);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const truncateName = (name) => (name.length > 48 ? name.substring(0, 10) + "..." : name);

  if (!memory) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={styles.memoryView}>
      <Box className={styles.topButtons}>
        <Button onClick={handleGoBack} className={styles.backButton}>
          <AiOutlineArrowLeft className={styles.backIcon} />
          <Box>Back</Box>
        </Button>

        <Box className={styles.extraButtons}>
          <Button onClick={handleCheckPage} className={styles.iconUploadButton}>
            <span className={styles.iconCircle}><AiOutlineUser /></span>
            <Box className={styles.buttonText}>Check Memory</Box>
          </Button>

          <Button onClick={handleSharePage} className={styles.iconUploadButton}>
            <span className={styles.iconCircle}><AiOutlineShareAlt /></span>
            <Box className={styles.buttonText}>Share Memory</Box>
          </Button>
        </Box>
      </Box>

      <Box className={styles.innerelement}>
        <Box className={styles.artParent}>
          <FallbackImage className={styles.artIcon} alt="" src={`${API_BASE}${memory.file_path}`} type={'image'} />
          <Box className={styles.auction}>
            <Box className={styles.title}>
              <Typography className={styles.titleText}>{memory.title}</Typography>
              <Typography className={styles.name}>{memory.description}</Typography>
            </Box>
            <Box className={styles.topBid}>
              <Typography className={styles.subtitle}>Price</Typography>
              <Typography className={styles.eth}>${memory.price}</Typography>
            </Box>
          </Box>
        </Box>

        <Box className={styles.sidebar}>
          <Box className={styles.activity}>
            <Tabs tabs={tabs} onTabChange={handleTabChange} />
            {selectedTab === "Details" && memory && (
              <Box className={styles.detailsSection}>
                <Typography variant="h6" className={styles.detailsTitle}>Description</Typography>
                <Typography className={styles.detailsContent}>{memory.description}</Typography>

                <Typography variant="h6" className={styles.detailsTitle} sx={{ marginTop: "1rem" }}>Availability</Typography>
                <Typography className={styles.detailsContent}>
                  {memory.amount === 0 ? "Unlimited" : (
                    <>
                      Total: {memory.amount}<br />
                      Sold: {memory.sold}<br />
                      Available: {memory.amount - memory.sold}
                    </>
                  )}
                </Typography>

                {memory.tags?.length > 0 && (
                  <>
                    <Typography variant="h6" className={styles.detailsTitle} sx={{ marginTop: "1rem" }}>Tags</Typography>
                    <Typography className={styles.detailsContent}>
                      {memory.tags.join(", ")}
                    </Typography>
                  </>
                )}
              </Box>
            )}
            {selectedTab === "Activity" && memory && (
              <Box className={styles.list}>
                {sales.map((item, index) => (
                  <Box key={index} className={styles.activityItem}>
                    <Box className={styles.topSection}>
                      <Box className={styles.bid}>Purchased:</Box>
                      <Box className={styles.price}>
                        <Typography className={styles.bidAmount}><b>{item.price} {item.currency}</b></Typography>
                      </Box>
                    </Box>
                    <Box className={styles.bottomSection}>
                      <Box className={styles.details}>
                        <Box className={styles.bid}>by</Box>
                        <Typography className={styles.link}><b>{item.buyer_name.trim() ? truncateName(item.buyer_name) : "Anonymous"}</b></Typography>
                        <Box className={styles.bid}>{new Date(item.sale_date).toLocaleString()}</Box>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Memory page link copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MemoryView;